import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { View, Text, FlatList, useWindowDimensions } from 'react-native';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useUnistyles } from 'react-native-unistyles';
import { useQueryClient } from '@tanstack/react-query';
import Toolbar from '@/components/Files/Toolbar';
import FileRow from '@/components/Files/FileRow';
import FolderRow from '@/components/Files/FolderRow';
import Breadcrumbs from '@/components/Files/Breadcrumbs';
import EmptyState from '@/components/Files/EmptyState';
import Toast from 'react-native-toast-message';
import { showAlert } from '@/utils/showAlert';
import UploadQueue from '@/components/Files/UploadQueue';
import CreateFolderModal from '@/components/Files/CreateFolderModal';
import RenameModal from '@/components/Files/RenameModal';
import MoveModal from '@/components/Files/MoveModal';
import RecentFiles from '@/components/Files/RecentFiles';
import { useFileUploads } from '@/hooks/useFileUploads';
import { pickDocument, openFile, shareFile } from '@/utils/FileManager';
import { supabase } from '@/data/client';
import { createFolder, updateFolder, renameFolder, moveFolder, deleteFolder, renameFile, moveFile, deleteFile, bulkDelete, bulkMove } from '@/data/files';
import { saveToRecents, removeFromRecents } from '@/utils/recentlyVisited';
import usePlatform from '@/utils/hooks/usePlatform';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SolidButton from '@/components/General/SolidButton';
export default function FilesScreen({ personID: propPersonID, embedded: propEmbedded }) {
  const { theme: { colors } } = useUnistyles();
  const router = useRouter();
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const params = useLocalSearchParams();
  const { isLandscape } = usePlatform();

  const scope = params.scope || (propPersonID ? 'person' : 'resources');
  const ownerID = params.ownerID === 'null' || params.ownerID === '' ? null : (params.ownerID || propPersonID || null);
  const folderID = params.folderID || 'root';
  const embedded = params.embedded === 'true' || propEmbedded || false;
  
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [sortKey, setSortKey] = useState('name-asc');
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  
  const [itemToRename, setItemToRename] = useState(null);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [itemToMove, setItemToMove] = useState(null);
  const [isBulkMove, setIsBulkMove] = useState(false);
  const [recentsKey, setRecentsKey] = useState(0);
  
  const { queue, enqueue, retry, cancel } = useFileUploads({ scope, ownerID, folderID });
  const { data: loggedInUser } = Query.people.loggedInUser();
  const canCreateFolder = !!loggedInUser?.can?.('Create Folders');

  // Get folder contents
  const { data: folderContents, isLoading } = Query.files.contents({ scope, ownerID, folderID });
  const { data: breadcrumbs } = Query.files.breadcrumbs({ folderID: folderID === 'root' ? null : folderID });
  const { data: searchResults } = Query.files.search({ query: searchQuery, scope, ownerID, rootFolderID: null });

  const handleNavigate = useCallback((targetFolderID) => {
    const url = targetFolderID
      ? `/files?scope=${scope}&ownerID=${ownerID || ''}&folderID=${targetFolderID}${embedded ? '&embedded=true' : ''}`
      : `/files?scope=${scope}&ownerID=${ownerID || ''}${embedded ? '&embedded=true' : ''}`;
    router.push(url);
  }, [scope, ownerID, embedded, router]);

  const handleOpenFile = useCallback((file) => {
    openFile({ key: file.key, filename: file.filename, mimeType: file.mimeType });
    saveToRecents({
      id: file.id,
      type: 'file',
      filename: file.filename,
      extension: file.extension,
      mimeType: file.mimeType,
      size: file.size,
      key: file.key,
    });
  }, []);

  const handleOpenRecentFile = useCallback(async (file) => {
    const { data } = await supabase.from('Files').select('id').eq('id', file.id).maybeSingle();
    if (!data) {
      Toast.show({ type: 'error', text2: 'This file has been removed' });
      await removeFromRecents(file.id);
      setRecentsKey(k => k + 1);
      return;
    }
    handleOpenFile(file);
  }, [handleOpenFile]);

  const handleDeleteFolder = useCallback((folder) => {
    showAlert({
      title: 'Delete Folder',
      subtitle: 'This will delete the folder and all its contents. Are you sure?',
      confirmText: 'Delete',
      isDestructive: true,
      onConfirm: async () => {
        const result = await deleteFolder({ id: folder.id });
        if (result) {
          for (const id of result) {
            await removeFromRecents(id);
          }
          setRecentsKey(k => k + 1);
          queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
          queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
        }
      }
    });
  }, [queryClient, scope, ownerID, folderID]);

  const handleDeleteFile = useCallback((file) => {
    showAlert({
      title: 'Delete File',
      subtitle: 'Are you sure you want to delete this file?',
      confirmText: 'Delete',
      isDestructive: true,
      onConfirm: async () => {
        const result = await deleteFile({ id: file.id, key: file.key });
        if (result) {
          await removeFromRecents(file.id);
          setRecentsKey(k => k + 1);
          queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
          queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
        }
      }
    });
  }, [queryClient, scope, ownerID, folderID]);

  const handleFilePress = useCallback((file, action) => {
    if (action === 'menu') {
      showAlert({
        title: 'File Options',
        subtitle: 'What would you like to do with this file?',
        extraButton: { text: 'Delete', isDestructive: true, onPress: () => handleDeleteFile(file) },
        confirmText: 'Open',
        onConfirm: () => handleOpenFile(file),
      });
    } else {
      handleOpenFile(file);
    }
  }, [handleDeleteFile, handleOpenFile]);

  const handleFolderPress = useCallback((folder, action) => {
    if (action === 'menu') {
      showAlert({
        title: 'Folder Options',
        subtitle: 'What would you like to do with this folder?',
        extraButton: { text: 'Delete', isDestructive: true, onPress: () => handleDeleteFolder(folder) },
        confirmText: 'Edit',
        onConfirm: () => { setFolderToEdit(folder); setShowCreateFolderModal(true); },
      });
    } else {
      handleNavigate(folder.id);
    }
  }, [handleDeleteFolder, handleNavigate]);

  const handleSelectFolder = useCallback((folder) => {
    setSelectedFolders(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(folder.id)) {
        newSelected.delete(folder.id);
      } else {
        newSelected.add(folder.id);
      }
      return newSelected;
    });
  }, []);

  const handleSelectFile = useCallback((file) => {
    setSelectedFiles(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(file.id)) {
        newSelected.delete(file.id);
      } else {
        newSelected.add(file.id);
      }
      return newSelected;
    });
  }, []);

  const handleClearSelection = () => {
    setSelectedFolders(new Set());
    setSelectedFiles(new Set());
  };

  // Header buttons (Upload + Create Folder)
  useEffect(() => {
    if (embedded) return;
    navigation.setOptions({
      headerRight: () => (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {isLandscape ? (
            <>
              <SolidButton size="sm" iconName="cloud-upload-outline" iconBefore text="Upload" onPress={handleUpload} />
              {canCreateFolder && <SolidButton size="sm" iconName="add" iconBefore text="New Folder" type="secondary" outline onPress={() => { setFolderToEdit(null); setShowCreateFolderModal(true); }} />}
            </>
          ) : (
            <DropdownMenu
              options={[
                ...(canCreateFolder ? [{ name: 'New Folder', icon: dropdownIcons.add, onSelect: () => { setFolderToEdit(null); setShowCreateFolderModal(true); } }] : []),
                { name: 'Upload File', icon: { web: 'cloud-upload-outline', ios: 'arrow.up.doc', android: 'cloud-upload-outline' }, onSelect: handleUpload },
              ]}
            />
          )}
        </View>
      ),
    });
  }, [navigation, embedded, isLandscape, canCreateFolder]);

  const handleUpload = async () => {
    const attachments = await pickDocument({ multiple: true });
    if (attachments.length > 0) {
      enqueue(attachments);
    }
  };

  const handleCreateFolder = async ({ name, roleIDs }) => {
    const result = folderToEdit
      ? await updateFolder({ id: folderToEdit.id, name, roleIDs })
      : await createFolder({ name, roleIDs, parentFolderID: folderID, scope, ownerID });
    if (result) {
      queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
    }
    setShowCreateFolderModal(false);
    setFolderToEdit(null);
  };

  const handleRename = async (name) => {
    const result = itemToRename.filename
      ? await renameFile({ id: itemToRename.id, newFilename: name })
      : await renameFolder({ id: itemToRename.id, name });
    if (result) {
      queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
    }
    setShowRenameModal(false);
    setItemToRename(null);
  };

  const handleMove = async (destinationFolderID) => {
    if (isBulkMove) {
      const result = await bulkMove({
        folderIDs: Array.from(selectedFolders),
        fileIDs: Array.from(selectedFiles),
        destinationFolderID
      });
      if (result) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID: destinationFolderID || 'root' }) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
      }
      handleClearSelection();
    } else if (itemToMove?.filename) {
      const result = await moveFile({ id: itemToMove.id, newFolderID: destinationFolderID });
      if (result) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID: destinationFolderID || 'root' }) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
      }
    } else if (itemToMove) {
      const result = await moveFolder({ id: itemToMove.id, newParentFolderID: destinationFolderID });
      if (result) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID: destinationFolderID || 'root' }) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
      }
    }
    setShowMoveModal(false);
    setItemToMove(null);
    setIsBulkMove(false);
  };

  const handleBulkDelete = () => {
    showAlert({
      title: 'Delete Items',
      subtitle: 'Are you sure you want to delete these items?',
      confirmText: 'Delete',
      isDestructive: true,
      onConfirm: async () => {
        const result = await bulkDelete({ folderIDs: Array.from(selectedFolders), fileIDs: Array.from(selectedFiles) });
        if (result?.success) {
          for (const id of result.deletedFileIDs) {
            await removeFromRecents(id);
          }
          setRecentsKey(k => k + 1);
          queryClient.invalidateQueries({ queryKey: QueryKeys.files.contents({ scope, ownerID, folderID }) });
          queryClient.invalidateQueries({ queryKey: QueryKeys.files.tree({ scope, ownerID }) });
        }
        handleClearSelection();
      }
    });
  };

  const sortedItems = useMemo(() => {
    const rawItems = searchQuery && searchQuery.length > 0
      ? { folders: searchResults?.folders || [], files: searchResults?.files || [] }
      : folderContents || { folders: [], files: [] };
    const sorter = (a, b) => {
      const nameA = (a.name || a.filename || '').toLowerCase();
      const nameB = (b.name || b.filename || '').toLowerCase();
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      if (sortKey === 'name-asc') return nameA.localeCompare(nameB);
      if (sortKey === 'name-desc') return nameB.localeCompare(nameA);
      if (sortKey === 'date-desc') return dateB - dateA;
      if (sortKey === 'date-asc') return dateA - dateB;
      return 0;
    };
    return [
      ...[...rawItems.folders].sort(sorter),
      ...[...rawItems.files].sort(sorter),
    ];
  }, [searchQuery, searchResults, folderContents, sortKey]);

  const totalSelected = selectedFolders.size + selectedFiles.size;
  const isAtRoot = folderID === 'root';
  const { width: screenWidth } = useWindowDimensions();

  // Calculate grid columns: 2 on mobile, as many as fit at 200px on desktop
  const maxCardWidth = Math.min(200, screenWidth / 2);
  const gridColumns = Math.floor((screenWidth - (embedded ? 0 : 32)) / maxCardWidth) || 2;

  const listHeader = useMemo(() => (
    <View style={{paddingHorizontal: embedded ? 8 : 16}}>
      <UploadQueue queue={queue} onRetry={retry} onCancel={cancel} />
      <Breadcrumbs breadcrumbs={breadcrumbs ?? []} onNavigate={handleNavigate} />
      {!embedded && isAtRoot && !searchQuery && (
        <RecentFiles key={recentsKey} onPress={handleOpenRecentFile} />
      )}
    </View>
  ), [embedded, queue, retry, cancel, breadcrumbs, handleNavigate, isAtRoot, searchQuery, recentsKey, handleOpenRecentFile]);

  const renderItem = useCallback(({ item }) => (
    item.filename ? (
      <FileRow
        file={item}
        viewMode={viewMode}
        onPress={handleFilePress}
        onLongPress={handleFilePress}
        isSelected={selectedFiles.has(item.id)}
        onSelect={handleSelectFile}
        onRename={(f) => { setItemToRename(f); setShowRenameModal(true); }}
        onMove={(f) => { setItemToMove(f); setShowMoveModal(true); }}
        onDelete={handleDeleteFile}
        onShare={(f) => shareFile({ key: f.key, filename: f.filename })}
      />
    ) : (
      <FolderRow
        folder={item}
        viewMode={viewMode}
        onPress={handleFolderPress}
        onLongPress={handleFolderPress}
        isSelected={selectedFolders.has(item.id)}
        onSelect={handleSelectFolder}
        onRename={(f) => { setFolderToEdit(f); setShowCreateFolderModal(true); }}
        onDelete={handleDeleteFolder}
      />
    )
  ), [viewMode, selectedFiles, selectedFolders, handleFilePress, handleSelectFile, handleDeleteFile, handleFolderPress, handleSelectFolder, handleDeleteFolder]);

  if (isLoading) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ color: colors.textSecondary }}>Loading...</Text>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: 'transparent' }}>
      {!embedded && <Toolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        viewMode={viewMode}
        onViewToggle={setViewMode}
        sortKey={sortKey}
        onSortChange={setSortKey}
        selectedCount={totalSelected}
        onBulkMove={() => { setIsBulkMove(true); setShowMoveModal(true); }}
        onBulkDelete={handleBulkDelete}
        onClearSelection={handleClearSelection}
      />}

      {embedded && (
        <View style={{ flexDirection: 'row', gap: 8, paddingVertical: 10 }}>
          <SolidButton testID="files-upload-button" size="sm" iconName="cloud-upload-outline" iconBefore text="Upload" onPress={handleUpload} />
          {canCreateFolder && <SolidButton testID="files-new-folder-button" size="sm" iconName="add" iconBefore text="New Folder" type="textSecondary" outline onPress={() => { setFolderToEdit(null); setShowCreateFolderModal(true); }} />}
        </View>
      )}

      <FlatList
        data={sortedItems}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={listHeader}
        key={viewMode}
        numColumns={viewMode === 'grid' ? gridColumns : 1}
        // columnWrapperStyle must not be present at all when numColumns === 1 —
        // RN throws "columnWrapperStyle not supported for single column lists"
        // even when the value is undefined (CHURCH-APP-1EP).
        {...(viewMode === 'grid' ? { columnWrapperStyle: { paddingHorizontal: embedded ? 8 : 8 } } : {})}
        contentContainerStyle={viewMode === 'grid' ? { paddingHorizontal: embedded ? 8 : 8 } : undefined}
        ListEmptyComponent={<EmptyState />}
        renderItem={renderItem}
      />

      <CreateFolderModal
        visible={showCreateFolderModal}
        onClose={() => { setShowCreateFolderModal(false); setFolderToEdit(null); }}
        onCreate={handleCreateFolder}
        folder={folderToEdit}
      />

      <RenameModal
        visible={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        onRename={handleRename}
        initialName={itemToRename?.name || itemToRename?.filename}
      />

      <MoveModal
        visible={showMoveModal}
        onClose={() => setShowMoveModal(false)}
        onMove={handleMove}
        scope={scope}
        ownerID={ownerID}
        excludeFolderID={itemToMove?.id}
      />

    </View>
  );
}
